import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// MUI
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

// Custom
import useFormController from '@/components/useFormController';
import FormContent, { FormContentColumns } from '@/components/FormContent';
import FormButton from '@/ui/Form/FormButton';
import Loader from '@/ui/Loader/Loader';
import { GridContainer } from '@/ui/Grid';
import DataController from '@/lib/DataController';
import Permissions from '@/lib/permissions';
import UserContext, { UserContextType } from '@/context/UserContext/UserContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import model from '@/models/groblja/upitnik_trazi';
import UpitnikNaslov from './UpitnikNaslov';

// Types
import { IFormLayout } from '@/@types/models/model';
import { PickerItem } from '@/@types/controls/controls';

// Services
import { upitnikService, UpitnikGroblje, UpitnikPolje, UpitnikTraziResult } from '@/services/upitnikService';

type NotFound = 'nije_pronadjen' | 'vise';

const SVA_POLJA: UpitnikPolje[] = ['polje', 'red', 'broj'];

const textOrNull = (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null);

// the picked groblje's fields, or while none is picked the ones all groblja share (none if they differ)
const getPolja = (groblja: UpitnikGroblje[], grobljeId: number | null): UpitnikPolje[] => {
  const izbor = grobljeId !== null ? groblja.filter((g) => g.value === grobljeId) : groblja;
  if (new Set(izbor.map((g) => g.upitnik_polja.join())).size !== 1) return [];
  return SVA_POLJA.filter((p) => izbor[0].upitnik_polja.includes(p));
};

type UpitnikTraziPoljaProps = {
  jedno: string | null;
  groblje: PickerItem | null;
  polja: UpitnikPolje[];
  onGrobljeChange: (groblje: PickerItem | null) => void;
};

const UpitnikTraziPolja = ({ jedno, groblje, polja, onGrobljeChange }: UpitnikTraziPoljaProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useContext(SnackbarContext);

  const fieldNames = jedno ? polja : ['groblje_id', ...polja];
  const layout: IFormLayout = [
    ...(jedno ? [] : [{ columns: 1 as FormContentColumns, fieldNames: ['groblje_id'] }]),
    ...(polja.length > 0 ? [{ columns: polja.length as FormContentColumns, fieldNames: polja }] : []),
  ];

  // only the asked fields are in the form, so only they get validated
  const [dc] = useState(() => new DataController({ ...model, forms: { ...model.forms, default: { fields: fieldNames } } }));
  const { record, validation, fields, onFieldChange, doValidate, pickerDataControllers } = useFormController({
    dc,
    form: 'default',
    mode: 'insert',
    fieldNames,
    initialRecord: groblje ? { groblje_id: groblje } : {},
    onClose: () => {},
  });

  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState<NotFound | null>(null);

  const handleFieldChange = (value: any, source: string) => {
    setNotFound(null);
    if (onFieldChange) {
      onFieldChange(value, source);
    }
    if (source === 'groblje_id') {
      onGrobljeChange((value as PickerItem | null) ?? null);
    }
  };

  const handleSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();
    if (!doValidate()) return;

    const data = dc.prepareSendingData(record);
    setSearching(true);
    upitnikService
      .trazi({
        groblje_id: data.groblje_id as number,
        polje: textOrNull(data.polje),
        red: textOrNull(data.red),
        broj: textOrNull(data.broj),
      })
      .then((resp) => {
        const result = resp.success ? (resp.data as UpitnikTraziResult) : null;
        if (result?.result === 'pronadjen') {
          // replace, so the back button skips the lookup and the upitnik loads like from the QR code
          navigate(`/groblja/upitnik/${result.gid}`, { replace: true });
          return;
        }
        setSearching(false);
        if (result) setNotFound(result.result);
        else showNotification(t('groblja:upitnik.greska'), 'error');
      });
  };

  return (
    <Card component="form" onSubmit={handleSubmit} noValidate>
      <CardContent>
        <UpitnikNaslov />
        <Typography variant="body2" gutterBottom>
          {t('groblja:upitnik.trazi.uvod')}
        </Typography>
        {jedno ? (
          <Typography variant="h6" sx={{ mt: 2 }}>
            {jedno}
          </Typography>
        ) : null}

        <Box mt={2}>
          <GridContainer>
            <FormContent
              fieldNames={fieldNames}
              record={record}
              validation={validation}
              fields={fields}
              layout={layout}
              onFieldChange={handleFieldChange}
              pickerDataControllers={pickerDataControllers}
              mode="insert"
            />
          </GridContainer>
        </Box>

        {notFound ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t(`groblja:upitnik.trazi.${notFound}`, { kontakt: t('groblja:upitnik.listic.kontakt') })}
          </Alert>
        ) : null}
      </CardContent>
      <CardActions>
        <FormButton type="submit" color="primary" variant="contained" fullWidth disabled={searching}>
          {t('groblja:upitnik.trazi.pronadji')}
        </FormButton>
      </CardActions>
      <Loader open={searching} />
    </Card>
  );
};

type UpitnikTraziFormProps = {
  groblja: UpitnikGroblje[];
};

const UpitnikTraziForm = ({ groblja }: UpitnikTraziFormProps) => {
  // a single groblje is picked already, the picker would only be noise
  const jedno = groblja.length === 1 ? groblja[0].label : null;
  const [groblje, setGroblje] = useState<PickerItem | null>(
    groblja.length === 1 ? { value: groblja[0].value, label: groblja[0].label } : null
  );
  const polja = getPolja(groblja, groblje ? (groblje.value as number) : null);

  // the form controller fixes its fields on mount, a groblje asking other fields gets a fresh form
  return <UpitnikTraziPolja key={polja.join()} jedno={jedno} groblje={groblje} polja={polja} onGrobljeChange={setGroblje} />;
};

const UpitnikTrazi = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext) as UserContextType;

  const [groblja, setGroblja] = useState<UpitnikGroblje[] | null>(null);
  const [failed, setFailed] = useState(false);

  // logged-in staff without the public permission would get a 403, and a 403 logs them out
  const canFill = userContext.hasAnyPermission([Permissions.GrobljaUpitnik]);

  useEffect(() => {
    if (!canFill) return;
    upitnikService.getTraziGroblja().then((resp) => {
      if (resp.success && Array.isArray(resp.data)) setGroblja(resp.data as UpitnikGroblje[]);
      else setFailed(true);
    });
  }, [canFill]);

  const message = (() => {
    if (!canFill) return 'groblja:upitnik.prijavljen';
    if (failed) return 'groblja:upitnik.greska';
    if (groblja && groblja.length === 0) return 'groblja:upitnik.trazi.nema_groblja';
    return null;
  })();

  if (message) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1">{t(message)}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!groblja) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" animation="wave" sx={{ fontSize: '2rem', width: '70%' }} />
          <Skeleton variant="text" animation="wave" sx={{ width: '90%', mb: 2 }} />
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" animation="wave" height={48} sx={{ mb: 2 }} />
          ))}
          <Skeleton variant="rounded" animation="wave" height={40} />
        </CardContent>
      </Card>
    );
  }

  return <UpitnikTraziForm groblja={groblja} />;
};

export default UpitnikTrazi;
